import { useMemo } from 'react';
import { Eye, Sparkles, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MiniChessBoard } from './MiniChessBoard';
import { BoardTheme } from '@/hooks/useCosmetics';
import { PieceStyle } from '@/lib/pieces';
import { AvatarDisplay } from '@/components/AvatarDisplay';

interface ShopPreviewPanelProps {
  previewTheme: BoardTheme | null;
  previewPieceStyle: PieceStyle;
  previewAccessory: { type: string; color?: string } | null;
  previewFrame: { type: string; color?: string } | null;
  currentTheme: BoardTheme | null;
  currentPieceStyle: PieceStyle;
  currentAccessory: { type: string; color?: string } | null;
  currentFrame: { type: string; color?: string } | null;
  onReset: () => void;
  isPreviewActive: boolean;
}

export function ShopPreviewPanel({
  previewTheme,
  previewPieceStyle,
  previewAccessory,
  previewFrame,
  currentTheme,
  currentPieceStyle,
  currentAccessory,
  currentFrame,
  onReset,
  isPreviewActive
}: ShopPreviewPanelProps) {
  // Use preview values if active, otherwise current
  const displayTheme = isPreviewActive ? previewTheme : currentTheme;
  const displayPieceStyle = isPreviewActive ? previewPieceStyle : currentPieceStyle;
  const displayAccessory = isPreviewActive ? previewAccessory : currentAccessory;
  const displayFrame = isPreviewActive ? previewFrame : currentFrame;

  const frameStyles = useMemo(() => {
    if (!displayFrame?.type || displayFrame.type === 'none') return {};
    
    const frameColors: Record<string, string> = {
      bronze: 'linear-gradient(135deg, #cd7f32, #8b4513, #cd7f32)',
      silver: 'linear-gradient(135deg, #c0c0c0, #808080, #c0c0c0)',
      gold: 'linear-gradient(135deg, #ffd700, #b8860b, #ffd700)',
      diamond: 'linear-gradient(135deg, #b9f2ff, #7df9ff, #b9f2ff)',
      rainbow: 'linear-gradient(135deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #8b00ff)',
    };

    return {
      background: frameColors[displayFrame.type] || frameColors.bronze,
      padding: '4px',
      borderRadius: '50%',
    };
  }, [displayFrame]);

  return (
    <Card className="sticky top-4 border-primary/20 bg-gradient-to-br from-card to-muted/30 overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 animate-pulse" />
      </div>
      
      <CardHeader className="relative pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Eye className="h-5 w-5 text-primary" />
          <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Live Preview
          </span>
          {isPreviewActive && (
            <Badge variant="secondary" className="ml-auto text-xs animate-pulse">
              <Sparkles className="h-3 w-3 mr-1" />
              Previewing
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="relative space-y-4">
        {/* Board Preview */}
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground font-medium">Board Theme</p>
          <div className="flex justify-center">
            <MiniChessBoard 
              boardTheme={displayTheme}
              pieceStyle={displayPieceStyle}
              size="lg"
              animated={true}
            />
          </div>
          {displayTheme && (
            <p className="text-center text-sm font-medium text-foreground/80">
              {displayTheme.name}
            </p>
          )}
        </div>

        {/* Avatar Preview */}
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground font-medium">Avatar & Frame</p>
          <div className="flex justify-center">
            <div 
              className="transition-all duration-500"
              style={frameStyles}
            >
              <AvatarDisplay 
                size="lg"
                accessory={displayAccessory}
                frame={displayFrame}
              />
            </div>
          </div>
          <div className="flex justify-center gap-2">
            {displayAccessory?.type && displayAccessory.type !== 'none' && (
              <Badge variant="outline" className="text-xs">
                {displayAccessory.type}
              </Badge>
            )}
            {displayFrame?.type && displayFrame.type !== 'none' && (
              <Badge variant="outline" className="text-xs">
                {displayFrame.type} frame
              </Badge>
            )}
          </div>
        </div>

        {/* Currently Equipped Info */}
        <div className="pt-2 border-t border-border/50 space-y-1">
          <p className="text-xs text-muted-foreground">Currently Equipped:</p>
          <p className="text-sm font-medium">{currentTheme?.name || 'Classic'}</p>
        </div>

        {/* Reset Button */}
        {isPreviewActive && (
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full"
            onClick={onReset}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset Preview
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
